import dayjs from 'dayjs'
import type { FormikHelpers } from 'formik'

import { useModalContext } from '@fourthwall/components'
import { isProduction } from '@fourthwall/utils'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'

import type { FormValues } from '../../../Domain.types'
import { UnavailableSlugModal } from '../components/UnavailableSlugModal'
import { useGetSlugAvailabilityMutation } from '../queries/useGetSlugAvailabilityMutation'

export const useInternalDomain = () => {
  const { getSlugAvailabilityMutation } = useGetSlugAvailabilityMutation()
  const { currentShop } = useCurrentShop()
  const { open } = useModalContext()

  const getDomainUrl = (slug: string = '') => {
    return `${slug}-shop.${isProduction() ? '' : 'staging.'}fourthwall.com`
  }

  const getIsChangingSlug = () => {
    if (!currentShop?.metafields['internal-domain-updated-at']) {
      return false
    }

    return (
      dayjs().diff(
        dayjs(currentShop.metafields['internal-domain-updated-at'] as unknown as string),
        'hours',
      ) < 1
    )
  }

  const getUnavailableMessage = (
    type: Extract<
      NonNullable<
        ReturnType<typeof useGetSlugAvailabilityMutation>['getSlugAvailabilityMutation']['data']
      >['result'],
      { type: 'UNAVAILABLE' }
    >['issue']['type'],
    slug: string,
  ) => {
    if (type === 'DOMAIN_ALREADY_EXISTS') {
      return 'This domain is already in use.'
    }

    if (type === 'SLUG_TOO_LONG') {
      return 'This domain is too long.'
    }

    // INVALID_SLUG
    return `Domain ${slug} is invalid.`
  }

  const checkSlugAvailability = (slug: string, formik: FormikHelpers<FormValues>) => {
    if (!slug) {
      return
    }

    return getSlugAvailabilityMutation.mutateAsync([{ text: slug }], {
      onSuccess: (data) => {
        if (data.result.type === 'UNAVAILABLE') {
          const suggestedSlug = data.result.suggestions[0]?.slug || ''

          open(UnavailableSlugModal, {
            suggestedSlug: `${suggestedSlug || ''}-shop.fourthwall.com`,
            subtitle: getUnavailableMessage(data.result.issue.type, slug),
            onConfirm: () => {
              formik.setStatus({ errors: { internalDomain: undefined } })
              formik.setFieldValue('internalDomain', suggestedSlug)
            },
            onCancel: () => {
              formik.setStatus({ errors: { internalDomain: 'A domain is not valid' } })
            },
          })
        }
      },
    })
  }

  return {
    isChangingSlug: getIsChangingSlug(),
    isCheckingSlugAvailability: getSlugAvailabilityMutation.isLoading,
    getDomainUrl,
    checkSlugAvailability,
  }
}
